import {
  AnalyticsComponent,
  AnalyticsFieldName,
  OnboardingV2AnalyticsEvent,
  OnboardingV2AnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { TextTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { useAnalytics } from "@/services/Analytics";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import { Button, Text } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import {
  getOnboardingIllustrationIcon,
  OnboardingIllustrationName,
} from "@/ui/theme/icons/illustrations/onboarding";
import { joinTestIDs } from "@/utils/string.util";

enum ExitOnboardingContent {
  ConfirmExit = "Save & leave",
  Description = "You can pick up where you left off when you're ready.",
  DismissExit = "Keep going",
  Title = "Leave for now?",
}

interface ExitOnboardingBottomSheetModalProps {
  isOpen: boolean;
  onBeforeExit?: () => void;
  onDismiss: () => void;
}

const baseTestID = joinTestIDs("exit-onboarding", TextTestID.BottomSheet);

const ExitOnboardingBottomSheetModal = ({
  isOpen,
  onBeforeExit,
  onDismiss,
}: ExitOnboardingBottomSheetModalProps) => {
  const { track } = useAnalytics();

  const handleDismissExit = () => {
    track(OnboardingV2AnalyticsEvent.ExitKeepGoingPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]: OnboardingV2AnalyticsPageView.Modal,
    });
    onDismiss();
  };

  const handleConfirmExit = () => {
    track(OnboardingV2AnalyticsEvent.ExitSaveAndLeavePressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]: OnboardingV2AnalyticsPageView.Modal,
    });
    onBeforeExit?.();
    router.back();
  };

  return (
    <BottomSheetModal
      dismissAccessibilityLabel="Dismiss exit onboarding alert"
      isCloseOnOverlayPressDisabled
      isOpen={isOpen}
      isSwipeDownToCloseDisabled
      onRequestClose={handleDismissExit}
      testID={baseTestID}
    >
      <VStack gap={18}>
        <HStack alignItems="center" gap={12}>
          {getOnboardingIllustrationIcon({
            iconName: OnboardingIllustrationName.Placeholder,
          })}
          <Text testID={joinTestIDs(baseTestID, TextTestID.Title)} variant="h5">
            {ExitOnboardingContent.Title}
          </Text>
        </HStack>

        <Text
          testID={joinTestIDs(baseTestID, TextTestID.Description)}
          variant="body"
        >
          {ExitOnboardingContent.Description}
        </Text>

        <VStack gap={12}>
          <Button
            isFullWidth
            onPress={handleDismissExit}
            testID={joinTestIDs(baseTestID, "dismiss-exit")}
            title={ExitOnboardingContent.DismissExit}
            variant="primary"
          />
          <Button
            isFullWidth
            onPress={handleConfirmExit}
            testID={joinTestIDs(baseTestID, "confirm-exit")}
            title={ExitOnboardingContent.ConfirmExit}
            variant="tertiary"
          />
        </VStack>
      </VStack>
    </BottomSheetModal>
  );
};

export { ExitOnboardingBottomSheetModal, ExitOnboardingContent };
